import {
  parseShortlistQrMapping,
  serializeShortlistQrMapping,
  type ShortlistQrMapping
} from './shortlistQrMapping';
import type { PublicTheme } from './themeSettings';

/**
 * System settings catalog: `definedAt` + built-in `default`, and a `codec` for a value the
 * backend keeps in another shape (e.g. JSON text). Services read through `parse` and write
 * through `serialize`, so callers only see the typed value.
 * Types for names/values are inferred from this tree — do not duplicate them by hand.
 */
export const settingCatalog = {
  defaults_booking_confirmation: {
    definedAt: 'workspace',
    default: '' as string
  },
  defaults_brand_image: {
    definedAt: 'workspace',
    default: '/images/atellio_brand_logo_dark.png'
  },
  defaults_country_code: { definedAt: 'workspace', default: 'us' },
  defaults_currency: { definedAt: 'workspace', default: 'GBP' },
  defaults_locale: { definedAt: 'workspace', default: 'en' },
  defaults_timezone: { definedAt: 'workspace', default: 'Europe/London' },
  role_missing_days: { definedAt: 'workspace', default: 0 },
  tags_fixed: { definedAt: 'workspace', default: true },
  docusign_template_id: { definedAt: 'workspace', default: '' },
  shortlist_qrcode_cs_mapping: {
    definedAt: 'workspace',
    default: { mapping: {}, options: {} } as ShortlistQrMapping,
    codec: {
      parse: parseShortlistQrMapping,
      serialize: serializeShortlistQrMapping
    }
  },

  defaults_callsheet_billing: { definedAt: 'team', default: '' },
  defaults_callsheet_social: { definedAt: 'team', default: '' },
  defaults_callsheet_hard_drive: { definedAt: 'team', default: '' },
  defaults_callsheet_hard_drive_specs: { definedAt: 'team', default: '' },
  defaults_callsheet_external_recipients: {
    definedAt: 'team',
    default: [] as string[]
  },
  public_brand_image: {
    definedAt: 'team',
    default: '/images/atellio_brand_logo_dark.png'
  },
  public_company_name: { definedAt: 'team', default: '' },
  public_team_name: { definedAt: 'team', default: '' },
  public_platform_colour: { definedAt: 'team', default: '#f1f1f1' },
  public_primary_colour: { definedAt: 'team', default: '#190026' },
  public_secondary_colour: { definedAt: 'team', default: '#d9e3f0' },
  custom_nike_arrival_board_logo: { definedAt: 'team', default: '' },
  custom_nike_arrival_board_duration: { definedAt: 'team', default: 0 },
  custom_nike_default_arrival_time: { definedAt: 'team', default: '' },
  public_theme: { definedAt: 'team', default: {} as PublicTheme }
} as const;

export type SettingCatalog = typeof settingCatalog;
export type SettingName = keyof SettingCatalog;

/** Catalog defaults are examples; writable values are the widened primitive/object type. */
type WidenSettingDefault<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends boolean
      ? boolean
      : T;

export type SettingValue<K extends SettingName> = WidenSettingDefault<
  SettingCatalog[K]['default']
>;
export type SettingDefinedAt<K extends SettingName> =
  SettingCatalog[K]['definedAt'];

export type WorkspaceSettingName = {
  [K in SettingName]: SettingDefinedAt<K> extends 'workspace' ? K : never;
}[SettingName];

export type TeamSettingName = {
  [K in SettingName]: SettingDefinedAt<K> extends 'team' ? K : never;
}[SettingName];

type SettingCodec = {
  parse: (wire: unknown) => unknown;
  serialize: (value: never) => unknown;
};

/** The stored form of a setting value, as read from the backend. */
export function decodeSettingValue(name: SettingName, wire: unknown): unknown {
  const entry: { codec?: SettingCodec } = settingCatalog[name] as object;
  return entry.codec ? entry.codec.parse(wire) : wire;
}

/** The form the backend stores for a setting value. */
export function encodeSettingValue(name: SettingName, value: unknown): unknown {
  const entry: { codec?: SettingCodec } = settingCatalog[name] as object;
  return entry.codec ? entry.codec.serialize(value as never) : value;
}

export function isSettingName(name: string): name is SettingName {
  return Object.prototype.hasOwnProperty.call(settingCatalog, name);
}
